import React, { useEffect, useMemo, useState } from "react";
import {
  FaWhatsapp,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaPaperPlane,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import axiosInstance from "../axios";
import Skeleton from "../ui/Skeleton";
import "../styles/contact.css";

const ContactSkeleton = () => {
  return (
    <section className="contact-section">
      <div className="container">
        <div className="contact-hero">
          <Skeleton className="contact-skeleton-kicker" />
          <Skeleton className="contact-skeleton-title" />
          <Skeleton className="contact-skeleton-subtitle" />
        </div>

        <div className="row g-4">
          <div className="col-lg-6">
            <div className="contact-card">
              <div className="contact-card-header">
                <Skeleton className="contact-skeleton-header-title" />
              </div>
              <div className="contact-card-body">
                <Skeleton className="contact-skeleton-input mb-3" />
                <Skeleton className="contact-skeleton-input mb-3" />
                <Skeleton className="contact-skeleton-textarea mb-3" />
                <Skeleton className="contact-skeleton-button" />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="contact-card">
              <div className="contact-card-header">
                <Skeleton className="contact-skeleton-header-title" />
              </div>
              <div className="contact-card-body">
                <Skeleton className="contact-skeleton-info-line mb-3" />
                <Skeleton className="contact-skeleton-info-line mb-3" />
                <Skeleton className="contact-skeleton-info-line mb-3" />
                <Skeleton className="contact-skeleton-whatsapp mb-4" />
                <Skeleton className="contact-skeleton-map" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchCompany = async () => {
      try {
        setLoadingCompany(true);
        setError("");

        const response = await axiosInstance.get("/company");
        const companyData = response.data?.data?.[0] || null;

        if (isMounted) {
          setCompany(companyData);
        }
      } catch (err) {
        console.error("Failed to load company:", err);
        if (isMounted) {
          setError("Gagal memuat data perusahaan. Silakan coba lagi.");
          setCompany(null);
        }
      } finally {
        if (isMounted) {
          setLoadingCompany(false);
        }
      }
    };

    fetchCompany();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitSuccess(null);
  };

  const sendEmail = async (data) => {
    const response = await axiosInstance.post("/send-email", data);
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(null);

    try {
      await sendEmail(formData);
      setSubmitSuccess(true);
      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } catch (err) {
      console.error("Send email failed:", err);
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappNumber = useMemo(() => {
    if (!company?.whatsApp_number) return "";
    return String(company.whatsApp_number).replace(/\D/g, "");
  }, [company]);

  const mapSrc = useMemo(() => {
    return company?.map_embed_url?.trim()
      ? company.map_embed_url
      : "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3162.740395766742!2d106.82947421512066!3d-6.200934495393612!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e6991c7eaa472c7%3A0x3de2c4b0fd9c991!2sJalan%20Raya%20Properti%20No.%20123%2C%20Jakarta%2C%20Indonesia!5e0!3m2!1sen!2sid!4v1674648534294!5m2!1sen!2sid";
  }, [company]);

  return (
    <div className="contact-page">
      <Helmet>
        <title>Kontak Kami - Urbania</title>
        <meta
          name="description"
          content="Hubungi Urbania untuk konsultasi, pertanyaan, dan layanan properti terbaik."
        />
        <meta property="og:title" content="Kontak Kami - Urbania" />
        <meta
          property="og:description"
          content="Hubungi Urbania untuk konsultasi dan informasi layanan properti."
        />
        <link rel="canonical" href="https://mitraproperti.co.id/contact" />
      </Helmet>

      {loadingCompany ? (
        <ContactSkeleton />
      ) : (
        <section className="contact-section">
          <div className="container">
            <div className="contact-hero">
              <div className="contact-kicker">Contact Us</div>
              <h1 className="contact-title">Hubungi Kami</h1>
              <p className="contact-subtitle">
                Kami siap membantu kebutuhan Anda seputar properti, konsultasi,
                pemasaran, dan layanan lainnya.
              </p>
            </div>

            {error && (
              <div className="contact-alert contact-alert-danger">{error}</div>
            )}

            <div className="row g-4">
              <div className="col-lg-6">
                <div className="contact-card">
                  <div className="contact-card-header">
                    <h2>Send Message</h2>
                  </div>

                  <div className="contact-card-body">
                    <form onSubmit={handleSubmit} className="contact-form">
                      <div className="form-group">
                        <label htmlFor="name">Nama</label>
                        <div className="input-wrap">
                          <FaPaperPlane className="input-icon" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Masukkan nama Anda"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <div className="input-wrap">
                          <FaEnvelope className="input-icon" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="nama@email.com"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Pesan</label>
                        <div className="input-wrap textarea-wrap">
                          <textarea
                            id="message"
                            name="message"
                            rows="5"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tulis pesan Anda di sini..."
                            required
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="contact-submit-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Mengirim..." : "Kirim Pesan"}
                      </button>

                      {submitSuccess !== null && (
                        <div
                          className={`contact-alert ${
                            submitSuccess
                              ? "contact-alert-success"
                              : "contact-alert-danger"
                          }`}
                        >
                          {submitSuccess
                            ? "Pesan berhasil dikirim!"
                            : "Terjadi kesalahan, silakan coba lagi."}
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="contact-card">
                  <div className="contact-card-header">
                    <h2>Kontak & Alamat</h2>
                  </div>

                  <div className="contact-card-body">
                    <div className="contact-info-grid">
                      <div className="contact-info-item">
                        <div className="contact-info-icon">
                          <FaMapMarkerAlt />
                        </div>
                        <div>
                          <span className="contact-info-label">Alamat</span>
                          <p className="contact-info-value">
                            {company?.address || "-"}
                          </p>
                        </div>
                      </div>

                      <div className="contact-info-item">
                        <div className="contact-info-icon">
                          <FaEnvelope />
                        </div>
                        <div>
                          <span className="contact-info-label">Email</span>
                          <p className="contact-info-value">
                            {company?.email || "-"}
                          </p>
                        </div>
                      </div>

                      <div className="contact-info-item">
                        <div className="contact-info-icon">
                          <FaPhoneAlt />
                        </div>
                        <div>
                          <span className="contact-info-label">Telepon</span>
                          <p className="contact-info-value">
                            {company?.phone_number || "-"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="contact-wa-card">
                      <div className="contact-wa-copy">
                        <h3>WhatsApp</h3>
                        <p>Hubungi kami langsung untuk respon lebih cepat.</p>
                      </div>

                      <div className="contact-wa-action">
                        {whatsappNumber ? (
                          <button
                            type="button"
                            onClick={() =>
                              window.open(`https://wa.me/${whatsappNumber}`, "_blank")
                            }
                            className="contact-wa-btn"
                          >
                            <FaWhatsapp />
                            <span>Hubungi Kami</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="contact-wa-btn disabled"
                            disabled
                          >
                            Nomor tidak tersedia
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="contact-map-wrap">
                      <iframe
                        src={mapSrc}
                        title="Lokasi Kantor Urbania"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Contact;